import type { OutboundMessage, OutboundStatus, OutboundView, Outcome, SendChannel } from '../types/outbox'

// Display words for the outbox, in a rep's language — "reply", "meeting booked", "no answer yet" — never the
// API's vocabulary. Pure: no React, no fetch, no arithmetic beyond counting what the API already sent.

/** What the API's outcome word means to the person who sent the message. */
export const OUTCOME_WORDS: Record<Outcome, string> = {
  advanced: 'reply',
  booked: 'meeting booked',
  'no-response': 'no answer yet',
  declined: 'declined',
  won: 'won',
}

/** The choices on the override control, in the order a rep would reach for them. */
export const OUTCOME_CHOICES: Outcome[] = ['advanced', 'booked', 'won', 'declined', 'no-response']

export function outcomeWords(outcome: Outcome | null): string {
  return outcome ? OUTCOME_WORDS[outcome] ?? outcome : 'not measured yet'
}

export const STATUS_WORDS: Record<OutboundStatus, string> = {
  drafted: 'draft',
  approved: 'approved',
  sent: 'sent',
  discarded: 'discarded',
}

export function statusWords(status: OutboundStatus): string {
  return STATUS_WORDS[status] ?? status
}

export function viaWords(via: SendChannel | null): string {
  switch (via) {
    case 'copy': return 'copied and sent by hand'
    case 'mailto': return 'sent from Outlook'
    case 'graph': return 'sent from your mailbox'
    default: return ''
  }
}

/** A mailto: link carrying the subject and the body, so "Open in Outlook" opens a message ready to send. */
export function mailtoHref(row: Pick<OutboundMessage, 'toAddress' | 'subject' | 'finalText'>): string {
  const params = new URLSearchParams()
  if (row.subject) params.set('subject', row.subject)
  if (row.finalText) params.set('body', row.finalText)
  const query = params.toString().replace(/\+/g, '%20')
  return `mailto:${encodeURIComponent(row.toAddress)}${query ? `?${query}` : ''}`
}

/** The three lists the Outbox page shows, newest first each. Discarded rows are not shown. */
export function groupByStatus(messages: OutboundView[]): { drafted: OutboundView[]; approved: OutboundView[]; sent: OutboundView[] } {
  const by = (s: OutboundStatus) => messages.filter((m) => m.row.status === s)
  return { drafted: by('drafted'), approved: by('approved'), sent: by('sent') }
}

/** "3 sent · 1 reply · 1 meeting booked" — the line under the page title. */
export function sentSummary(sent: OutboundView[]): string {
  if (sent.length === 0) return 'Nothing sent yet.'
  const counts = new Map<Outcome, number>()
  for (const m of sent) if (m.row.outcome) counts.set(m.row.outcome, (counts.get(m.row.outcome) ?? 0) + 1)
  const parts = [`${sent.length} sent`]
  for (const o of OUTCOME_CHOICES) {
    const n = counts.get(o)
    if (n) parts.push(`${n} ${OUTCOME_WORDS[o]}${o === 'advanced' && n > 1 ? 'ies' : ''}`.replace('replyies', 'replies'))
  }
  const waiting = sent.filter((m) => !m.row.outcome).length
  if (waiting > 0) parts.push(`${waiting} waiting`)
  return parts.join(' · ')
}
